import React, { useState } from 'react';
import CommonModal from '../../../Components/CommonModal';
import CommonLoader from '../../../Components/CommonLoader';
import { risk_portal_project_create, risk_portal_project_update } from '../../../services';

export const PROJECT_TYPE_OPTIONS = [
    { value: 'small', label: 'Small' },
    { value: 'medium', label: 'Medium' },
    { value: 'large', label: 'Large' },
    { value: 'critical', label: 'Critical' },
    { value: 'ongoing', label: 'Ongoing / BAU' },
];

export const LIFECYCLE_OPTIONS = [
    { value: 'standard', label: 'Standard' },
    { value: 'agile', label: 'Agile' },
    { value: 'hybrid', label: 'Hybrid' },
    { value: 'small_project', label: 'Small Project' },
];

// ProjectForm — Add/Edit a project. CommonModal (config-driven fields). Create
// via risk_portal_project_create, edit via risk_portal_project_update. Mount
// fresh per open (parent passes a `key`) so defaultValues reflect the current
// `editing` record. Page owns the toast; this reports via callbacks.
//
// Props: show, onClose, onSuccess, onError, editing (project|null), businessUnits.
export default function ProjectForm({ show, onClose, onSuccess, onError, editing, businessUnits = [] }) {
    const [busy, setBusy] = useState(false);
    const isEdit = !!editing?.id;

    const buOptions = [
        { value: '', label: '(default — first BU)' },
        ...businessUnits.map((b) => ({ value: String(b.id), label: b.code ? `${b.name} (${b.code})` : b.name })),
    ];

    const submit = async (data) => {
        setBusy(true);
        try {
            const payload = {
                name: (data.name || '').trim(),
                code: (data.code || '').trim(),
                project_type: data.project_type || 'medium',
                project_lifecycle: data.project_lifecycle || 'standard',
                location: (data.location || '').trim(),
                project_manager: (data.project_manager || '').trim(),
                business_unit_id: data.business_unit_id ? parseInt(data.business_unit_id, 10) : null,
            };
            if (isEdit) await risk_portal_project_update(editing.id, payload);
            else await risk_portal_project_create(payload);
            onSuccess?.(isEdit ? `Project "${payload.name}" updated` : `Project "${payload.name}" created`);
            onClose?.();
        } catch (e) {
            onError?.(e?.response?.data?.error?.message || e?.response?.data?.response_data?.message || 'Failed to save project');
        } finally {
            setBusy(false);
        }
    };

    return (
        <CommonModal
            show={show}
            onHide={onClose}
            mid
            title={isEdit ? 'Edit Project' : 'Add Project'}
            subtitle="Only Name is required — everything else is optional and can be filled in later."
            fields={[
                { name: 'name', label: 'Name', type: 'text', half: true, required: 'Project name is required.' },
                { name: 'code', label: 'Code', type: 'text', half: true, placeholder: 'auto-generated if blank' },
                { name: 'project_type', label: 'Project Type', type: 'select', half: true, options: PROJECT_TYPE_OPTIONS },
                { name: 'project_lifecycle', label: 'Lifecycle', type: 'select', half: true, options: LIFECYCLE_OPTIONS },
                { name: 'business_unit_id', label: 'Business Unit', type: 'select', half: true, options: buOptions },
                { name: 'project_manager', label: 'Project Manager', type: 'text', half: true, placeholder: 'name or email' },
                { name: 'location', label: 'Location', type: 'text', placeholder: 'e.g. Bangalore, EMEA' },
            ]}
            defaultValues={{
                name: editing?.name || '',
                code: editing?.code || '',
                project_type: editing?.project_type || 'medium',
                project_lifecycle: editing?.project_lifecycle || 'standard',
                business_unit_id: editing?.business_unit_id ? String(editing.business_unit_id) : '',
                project_manager: editing?.project_manager || '',
                location: editing?.location || '',
            }}
            onSubmit={submit}
            buttons={[
                { text: 'Cancel', variant: 'secondary', onClick: onClose },
                { text: isEdit ? 'Save' : 'Create', variant: 'primary', type: 'submit' },
            ]}
        >
            {busy && <CommonLoader overlay label="Saving…" />}
        </CommonModal>
    );
}
